import { Figure } from './figures/Figure'
import { Icon } from './Icon'
import type { HelpEntry } from '../lib/help'
import { tutorialFor, type TutorialId } from '../lib/tutorials'

/**
 * What this screen is for, read on the screen it is about.
 *
 * A lead, a figure where a shape says it faster, a few points, the one thing most often misread, and
 * the walkthroughs this screen is a step of. The full list of walkthroughs is no longer here: it is in
 * the Help menu, where somebody can find it without first asking about a screen.
 *
 * Deliberately knows nothing about the dialog it is rendered in, and takes an entry rather than
 * reading the route itself, so moving it into another container is a change to one wrapper.
 */
export function HelpPanel({
  entry,
  onStartTutorial
}: {
  entry: HelpEntry
  onStartTutorial: (id: TutorialId) => void
}) {
  const offered = entry.tutorials ?? []

  return (
    <div className="help-panel" data-testid="help-panel">
      <p className="help-lead" data-testid="help-lead">
        {entry.lead}
      </p>

      {entry.figure && <Figure spec={entry.figure} />}

      <ul className="help-list" data-testid="help-points">
        {entry.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>

      {/*
        The one thing about this screen most often misread, where there is one. A correction, never a
        consequence: a sentence that would change whether somebody presses a button stays on the page,
        because a warning only seen after asking is a warning too late (D-423). The bulb says "worth
        knowing", and deliberately not the warning glyph a consequence would carry.
      */}
      {entry.caveat && (
        <p className="help-caveat" data-testid="help-caveat">
          <Icon name="lightbulb" size={16} className="help-caveat-icon" />
          <span>{entry.caveat}</span>
        </p>
      )}

      {offered.length > 0 && (
        <div className="help-tutorials" data-testid="help-tutorials">
          <span className="help-eyebrow">Walkthroughs for this screen</span>
          <div className="help-chips">
            {offered.map((id) => {
              const tutorial = tutorialFor(id)
              return (
                <button
                  key={id}
                  type="button"
                  className="help-chip"
                  onClick={() => onStartTutorial(id)}
                  data-testid={`help-panel-tutorial-${id}`}
                >
                  <Icon name={tutorial.icon} size={15} />
                  {tutorial.title}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
